<template>
  <v-dialog v-model="value" width="750" persistent scrollable>
    <v-form v-model="valid" ref="form">
      <v-card class="w-100 rounded-lg">
        <v-card-title>{{ dialogTitle }}</v-card-title>
        <v-card-text>
          <v-progress-linear v-if="detailsLoading" indeterminate color="primary" class="mb-4" />

          <template v-if="!detailsLoading">
            <v-text-field
              v-model="form.name"
              :label="$t('spedilink.links.nameLabel')"
              outlined
              dense
              class="mb-2"
              :rules="nameRules"
              :disabled="isFieldDisabled"
            />
            <v-text-field
              v-model="form.description"
              :label="$t('spedilink.links.descriptionLabel')"
              outlined
              dense
              class="mb-6"
              :rules="descriptionRules"
              :disabled="isFieldDisabled"
            />

            <v-checkbox
              v-if="showCompanyShareCheckbox"
              v-model="form.companyShare"
              :label="$t('spedilink.links.companyShare')"
              hide-details
              class="mt-0 mb-4"
              :disabled="isReadOnly"
            />

            <template v-if="mode === 'edit'">
              <div class="subtitle-2 mb-2">{{ $t('spedilink.links.personsSection') }}</div>

              <v-data-table
                :items="form.users"
                :headers="userHeaders"
                hide-default-footer
                disable-pagination
                dense
                class="mb-2"
              >
                <template v-slot:[`item.status`]="{ item }">
                  <v-tooltip v-if="item.removed" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        color="warning"
                        v-bind="attrs"
                        v-on="on"
                      >
                        mdi-close
                      </v-icon>
                    </template>
                    <span>{{ $t('spedilink.persons.removedTooltip') }}</span>
                  </v-tooltip>
                  <v-tooltip v-if="item.blocked" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        color="error"
                        class="ml-1"
                        v-bind="attrs"
                        v-on="on"
                      >
                        mdi-block-helper
                      </v-icon>
                    </template>
                    <span>{{ $t('spedilink.persons.blockedTooltip') }}</span>
                  </v-tooltip>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                  <v-btn icon small @click="removePerson(item)">
                    <v-icon small color="error">mdi-close</v-icon>
                  </v-btn>
                </template>
                <template v-slot:no-data>
                  <span class="grey--text">{{ $t('spedilink.links.noData') }}</span>
                </template>
              </v-data-table>

              <v-autocomplete
                v-model="addPersonValue"
                :items="availablePersons"
                :loading="personsLoading"
                :search-input.sync="personsSearch"
                item-value="id"
                :item-text="personDisplayText"
                :label="$t('spedilink.links.addPerson')"
                :placeholder="$t('spedilink.links.searchPersons')"
                outlined
                dense
                clearable
                hide-no-data
                class="mb-6"
                @change="onPersonSelected"
                @update:search-input="onPersonsSearch"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.firstName }} {{ item.lastName }}</v-list-item-title>
                    <v-list-item-subtitle>{{ item.email }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </template>

            <div class="subtitle-2 mb-2">{{ $t('spedilink.links.groupsSection') }}</div>

            <v-data-table
              :items="form.groups"
              :headers="groupHeaders"
              hide-default-footer
              disable-pagination
              dense
              :show-expand="showGroupExpand"
              class="mb-2"
            >
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn v-if="mode === 'edit'" icon small @click="removeGroup(item)">
                  <v-icon small color="error">mdi-close</v-icon>
                </v-btn>
              </template>
              <template v-slot:expanded-item="{ item }">
                <td :colspan="groupHeaders.length + 1" class="pa-4">
                  <div v-if="item.companyShare && item.users && item.users.length > 0">
                    <div class="subtitle-2 mb-2">{{ $t('spedilink.links.members') }}</div>
                    <v-simple-table dense>
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th>{{ $t('spedilink.persons.firstName') }}</th>
                            <th>{{ $t('spedilink.persons.lastName') }}</th>
                            <th>{{ $t('spedilink.persons.email') }}</th>
                            <th>{{ $t('spedilink.persons.phone') }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="user in item.users" :key="user.id">
                            <td>{{ user.firstName }}</td>
                            <td>{{ user.lastName }}</td>
                            <td>{{ user.email }}</td>
                            <td>{{ user.phone || '-' }}</td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </div>
                  <div v-else-if="mode === 'edit' && getResignedMembers(item).length > 0">
                    <div class="subtitle-2 mb-2">{{ $t('spedilink.groups.resignedMembers') }}</div>
                    <v-simple-table dense>
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th>{{ $t('spedilink.persons.firstName') }}</th>
                            <th>{{ $t('spedilink.persons.lastName') }}</th>
                            <th>{{ $t('spedilink.persons.email') }}</th>
                            <th>{{ $t('spedilink.persons.phone') }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="user in getResignedMembers(item)" :key="user.id">
                            <td>{{ user.firstName }}</td>
                            <td>{{ user.lastName }}</td>
                            <td>{{ user.email }}</td>
                            <td>{{ user.phone || '-' }}</td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </div>
                  <div v-else class="grey--text">
                    {{ $t('spedilink.groups.noResignedMembers') }}
                  </div>
                </td>
              </template>
              <template v-slot:no-data>
                <span class="grey--text">{{ $t('spedilink.links.noData') }}</span>
              </template>
            </v-data-table>

            <template v-if="mode === 'edit'">
              <v-autocomplete
                v-model="addGroupValue"
                :items="availableOwnGroups"
                :loading="ownGroupsLoading"
                :search-input.sync="ownGroupsSearch"
                item-value="id"
                item-text="name"
                :label="$t('spedilink.links.addOwnGroup')"
                :placeholder="$t('spedilink.links.searchGroups')"
                outlined
                dense
                clearable
                hide-no-data
                class="mb-4"
                @change="onOwnGroupSelected"
                @update:search-input="onOwnGroupsSearch"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle v-if="item.description">{{ item.description }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>

              <v-autocomplete
                v-model="addCompanyGroupValue"
                :items="availableCompanyGroups"
                :loading="companyGroupsLoading"
                :search-input.sync="companyGroupsSearch"
                item-value="id"
                :item-text="companyGroupDisplayText"
                :label="$t('spedilink.links.addCompanyGroup')"
                :placeholder="$t('spedilink.links.searchGroups')"
                outlined
                dense
                clearable
                hide-no-data
                class="mb-2"
                @change="onCompanyGroupSelected"
                @update:search-input="onCompanyGroupsSearch"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ formatGroupOwner(item) }}
                      <span v-if="item.description"> — {{ item.description }}</span>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </template>
          </template>
        </v-card-text>
        <v-card-actions class="d-flex justify-end px-4 pb-4">
          <v-btn outlined :disabled="loading" @click="close()">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn
            v-if="!isReadOnly"
            color="primary"
            class="ml-2"
            :loading="loading"
            :disabled="loading || detailsLoading"
            depressed
            @click="save()"
          >
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <Confirm ref="confirm" />
  </v-dialog>
</template>

<script>
import Service from '@/services'
import Confirm from '@/components/dialogs/Confirm'

export default {
  name: 'EditSpedilinkLinkDialog',
  components: {
    Confirm
  },
  props: {
    value: {
      type: Boolean,
      required: true
    },
    linkId: {
      type: String,
      default: null
    },
    mode: {
      type: String,
      default: 'edit',
      validator: (value) => ['edit', 'manageSharing', 'view'].includes(value)
    }
  },
  data () {
    return {
      valid: true,
      loading: false,
      detailsLoading: false,
      initialCompanyShare: false,
      form: {
        name: '',
        description: '',
        companyShare: false,
        users: [],
        groups: []
      },
      addPersonValue: null,
      addGroupValue: null,
      addCompanyGroupValue: null,
      persons: [],
      personsLoading: false,
      personsSearch: '',
      personsSearchTimeout: null,
      ownGroups: [],
      ownGroupsLoading: false,
      ownGroupsSearch: '',
      ownGroupsSearchTimeout: null,
      companyGroups: [],
      companyGroupsLoading: false,
      companyGroupsSearch: '',
      companyGroupsSearchTimeout: null,
      nameRules: [
        (v) => !!v || this.$t('spedilink.links.nameRequired'),
        (v) => !v || v.length <= 200 || this.$t('spedilink.links.nameTooLong')
      ],
      descriptionRules: [
        (v) => !v || v.length <= 1000 || this.$t('spedilink.links.descriptionTooLong')
      ],
      userHeaders: [
        { text: '', value: 'status', width: '40', sortable: false },
        { text: this.$t('spedilink.persons.firstName'), value: 'firstName' },
        { text: this.$t('spedilink.persons.lastName'), value: 'lastName' },
        { text: this.$t('spedilink.persons.email'), value: 'email' },
        { text: this.$t('spedilink.persons.phone'), value: 'phone' },
        { text: '', align: 'end', width: '50', value: 'actions', sortable: false }
      ],
      groupHeaders: [
        { text: this.$t('spedilink.groups.name'), value: 'name' },
        { text: this.$t('spedilink.groups.description'), value: 'description' },
        { text: '', align: 'end', width: '50', value: 'actions', sortable: false }
      ]
    }
  },
  computed: {
    isReadOnly () {
      return this.mode === 'view'
    },
    isFieldDisabled () {
      return this.mode === 'view' || this.mode === 'manageSharing'
    },
    isManageSharingMode () {
      return this.mode === 'manageSharing'
    },
    showCompanyShareCheckbox () {
      return this.mode === 'edit' || this.mode === 'manageSharing'
    },
    showGroupExpand () {
      return this.mode === 'edit' || this.mode === 'manageSharing' || this.mode === 'view'
    },
    dialogTitle () {
      if (this.mode === 'manageSharing') {
        return this.$t('spedilink.links.manageSharingTitle')
      }
      if (this.mode === 'view') {
        return this.$t('spedilink.links.viewTitle')
      }
      return this.$t('spedilink.links.editTitle')
    },
    availablePersons () {
      const currentIds = this.form.users.map(u => u.id)
      return this.persons.filter(p => !currentIds.includes(p.id))
    },
    availableOwnGroups () {
      const currentIds = this.form.groups.map(g => g.id)
      return this.ownGroups.filter(g => !currentIds.includes(g.id))
    },
    availableCompanyGroups () {
      const currentIds = this.form.groups.map(g => g.id)
      return this.companyGroups.filter(g => !currentIds.includes(g.id))
    }
  },
  watch: {
    value (newVal) {
      if (newVal && this.linkId) {
        this.loadDetails()
        if (this.mode === 'edit') {
          this.loadPersons()
          this.loadOwnGroups()
          this.loadCompanyGroups()
        }
      } else if (!newVal) {
        this.resetForm()
      }
    }
  },
  methods: {
    getResignedMembers (group) {
      if (!group.users || !Array.isArray(group.users)) return []
      return group.users.filter(user => user.removed === true)
    },
    personDisplayText (item) {
      return `${item.firstName} ${item.lastName} (${item.email})`
    },
    onPersonsSearch (val) {
      clearTimeout(this.personsSearchTimeout)
      this.personsSearchTimeout = setTimeout(() => {
        this.loadPersons(val)
      }, 400)
    },
    companyGroupDisplayText (item) {
      const owner = this.formatGroupOwner(item)
      return owner ? `${item.name} (${owner})` : item.name
    },
    formatGroupOwner (group) {
      const first = group.ownerFirstName || ''
      const last = group.ownerLastName || ''
      return `${first} ${last}`.trim()
    },
    onOwnGroupsSearch (val) {
      clearTimeout(this.ownGroupsSearchTimeout)
      this.ownGroupsSearchTimeout = setTimeout(() => {
        this.loadOwnGroups(val)
      }, 400)
    },
    onCompanyGroupsSearch (val) {
      clearTimeout(this.companyGroupsSearchTimeout)
      this.companyGroupsSearchTimeout = setTimeout(() => {
        this.loadCompanyGroups(val)
      }, 400)
    },
    async loadDetails () {
      this.detailsLoading = true
      try {
        const { data } = await Service.get(`spedilink/SpediLinks/details/${this.linkId}`)
        this.form.name = data.name
        this.form.description = data.description || ''
        this.form.companyShare = data.companyShare || false
        this.initialCompanyShare = data.companyShare || false
        this.form.users = data.users || []
        this.form.groups = data.groups || []
      } catch (e) {
      } finally {
        this.detailsLoading = false
      }
    },
    async loadPersons (search) {
      this.personsLoading = true
      try {
        const params = { page: 1, pageSize: 50 }
        if (search) params.search = search
        const { data } = await Service.get('spedilink/SpediLinkUsers/GetList', params)
        this.persons = data.results
      } catch (e) {
      } finally {
        this.personsLoading = false
      }
    },
    async loadOwnGroups (search) {
      this.ownGroupsLoading = true
      try {
        const params = { page: 1, pageSize: 50, scope: 'own' }
        if (search) params.search = search
        const { data } = await Service.get('spedilink/SpediLinkGroups/GetList', params)
        this.ownGroups = data.results
      } catch (e) {
      } finally {
        this.ownGroupsLoading = false
      }
    },
    async loadCompanyGroups (search) {
      this.companyGroupsLoading = true
      try {
        const params = { page: 1, pageSize: 50, scope: 'company' }
        if (search) params.search = search
        const { data } = await Service.get('spedilink/SpediLinkGroups/GetList', params)
        this.companyGroups = data.results
      } catch (e) {
      } finally {
        this.companyGroupsLoading = false
      }
    },
    onPersonSelected (id) {
      if (!id) return
      const person = this.persons.find(p => p.id === id)
      if (person && !this.form.users.find(u => u.id === id)) {
        this.form.users.push(person)
      }
      this.$nextTick(() => {
        this.addPersonValue = null
        this.personsSearch = ''
      })
    },
    onOwnGroupSelected (id) {
      if (!id) return
      const group = this.ownGroups.find(g => g.id === id)
      if (group && !this.form.groups.find(g => g.id === id)) {
        this.form.groups.push(group)
      }
      this.$nextTick(() => {
        this.addGroupValue = null
        this.ownGroupsSearch = ''
      })
    },
    onCompanyGroupSelected (id) {
      if (!id) return
      const group = this.companyGroups.find(g => g.id === id)
      if (group && !this.form.groups.find(g => g.id === id)) {
        this.form.groups.push(group)
      }
      this.$nextTick(() => {
        this.addCompanyGroupValue = null
        this.companyGroupsSearch = ''
      })
    },
    removePerson (person) {
      this.form.users = this.form.users.filter(u => u.id !== person.id)
    },
    removeGroup (group) {
      this.form.groups = this.form.groups.filter(g => g.id !== group.id)
    },
    close () {
      this.$emit('close')
      this.resetForm()
    },
    resetForm () {
      this.form = { name: '', description: '', companyShare: false, users: [], groups: [] }
      this.initialCompanyShare = false
      this.addPersonValue = null
      this.addGroupValue = null
      this.addCompanyGroupValue = null
      this.persons = []
      this.ownGroups = []
      this.companyGroups = []
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },
    async confirmUnshareIfNeeded () {
      if (!this.initialCompanyShare || this.form.companyShare) {
        return true
      }
      const confirmed = await this.$refs.confirm.open(
        this.$t('spedilink.links.unshareConfirmTitle'),
        this.$t('spedilink.links.unshareConfirmMessage', { name: this.form.name })
      )
      return confirmed
    },
    async save () {
      this.$refs.form.validate()
      if (!this.valid) return

      const confirmed = await this.confirmUnshareIfNeeded()
      if (!confirmed) return

      this.loading = true
      try {
        if (this.isManageSharingMode) {
          if (!this.form.companyShare) {
            await Service.put('spedilink/SpediLinks/Unshare', { id: this.linkId })
          } else {
            this.close()
            return
          }
        } else {
          await Service.put('spedilink/SpediLinks/Update', {
            id: this.linkId,
            name: this.form.name,
            description: this.form.description || '',
            userIds: this.form.users.map(u => u.id),
            groupIds: this.form.groups.map(g => g.id),
            companyShare: this.form.companyShare
          })
        }
        await this.$store.dispatch('setSuccessNotification', this.$t('common.confirmOperation'))
        this.$emit('link-updated')
        this.resetForm()
      } catch (e) {
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
